import { z } from 'zod';
import { baseEntityFields, idSchema, isoDateTimeSchema } from './common.schema';
/**
 * Per-user API tokens (PRD §2.11 — P0): hashed at rest, auto-disabled on
 * inactivity, audited. The raw token is shown exactly once at creation.
 * Machine traffic from collectors uses collector tokens instead
 * (tech-arch §3) — user JWTs never authenticate machine traffic.
 *
 * API tokens are TENANT-SCOPED credentials: authenticating with one
 * grants only the user's membership in `tenantId` (least privilege —
 * a script credential minted for tenant A can never touch tenant B,
 * and global roles do not ride along).
 */
export const apiTokenInputSchema = z
    .object({
    name: z.string().min(1).max(200),
    /** Optional expiry; tokens also auto-disable after inactivity. */
    expiresAt: isoDateTimeSchema.optional(),
})
    .strict();
export const apiTokenSchema = z
    .object({
    name: z.string().min(1).max(200),
    userId: idSchema,
    /** SHA-256 of the token, lowercase hex. The token itself is never stored. */
    tokenHash: z.string().regex(/^[a-f0-9]{64}$/, 'must be lowercase SHA-256 hex'),
    /** Display prefix: "det_" + first token chars (e.g. "det_a1b2c3d4"). */
    tokenPrefix: z
        .string()
        .regex(/^det_[A-Za-z0-9_-]{4,12}$/, 'must be "det_" + 4-12 token chars'),
    lastUsedAt: isoDateTimeSchema.nullable(),
    expiresAt: isoDateTimeSchema.nullable(),
    disabledAt: isoDateTimeSchema.nullable(),
    disabledReason: z.enum(['manual', 'inactivity', 'expired']).nullable(),
})
    .extend(baseEntityFields)
    .strict()
    .superRefine((val, ctx) => {
    // A disabled token must say when AND why; an active token carries
    // neither — anything else corrupts the audit trail.
    if ((val.disabledAt === null) !== (val.disabledReason === null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['disabledReason'],
            message: 'disabledAt and disabledReason must be set together',
        });
    }
});
//# sourceMappingURL=api-token.schema.js.map